import { S2SafeParsable } from 'libs/end-to-end/src/interfaces';
import { BaseError } from 'libs/end-to-end/src/types/errors/base-error';
import { Result } from 'libs/end-to-end/src/result';
import { MyUser } from 'libs/end-to-end/src/my-user';
import { FeatureFlags } from 'libs/end-to-end/src/feature-flag';
import { ObservationLocationType } from 'libs/end-to-end/src/observation-upload-type';
import React, { createContext } from 'react';

export class UserCallbacks {
  readonly onLoginError: OnLoginErrorFn[] = [];

  addOnLoginError(callback: OnLoginErrorFn) {
    this.onLoginError.push(callback);
  }

  removeOnLoginError(callback: OnLoginErrorFn) {
    this.onLoginError.filter((cb) => cb !== callback);
  }
}

export interface StateAuthenticatedApiCallOptions<TResponse> {
  responseSchema: S2SafeParsable<unknown, TResponse>;
  url: string;
  requestOptions?: RequestInit;
}

export type OnLoginErrorFn = (error: BaseError) => void;

export interface UserHookOptions {
  onError?: OnLoginErrorFn;
}

export type UserAuthenticatedApiCall = <TResponse>(
  options: StateAuthenticatedApiCallOptions<TResponse>,
) => Promise<Result<TResponse, BaseError> | null>;

export type UserStatus =
  | 'logged_in'
  | 'logging_in'
  | 'logged_out'
  | 'logging_out'
  | null;

export interface UserHook {
  myUserStatus: UserStatus;
  logoutLink: string | null;
  myUser: MyUser | null;
  isAuthenticated: boolean;
  featureFlags: FeatureFlags | null;
  login: (email: string, password: string) => void;
  loginWithSSO: (code: string, redirectUri: string) => void;
  logout: () => void;
  startLoggingOut: (logoutUri: string) => void;
  userAuthenticatedApiCall: UserAuthenticatedApiCall;
  loadFeatureFlags: () => void;
  loadUser: (params: {
    observation: ObservationLocationType | null;
    organizationId: string | null;
    onLoaded?: (newObservation: ObservationLocationType | null) => void;
  }) => void;
}

export interface UserContext {
  myUserStatus:
    | 'logged_in'
    | 'logging_in'
    | 'logged_out'
    | 'logging_out'
    | null;
  logoutLink: string | null;
  myUser: MyUser | null;
  isAuthenticated: boolean;
  featureFlags: FeatureFlags | null;
  login: (email: string, password: string) => void;
  loginWithSSO: (code: string, redirectUri: string) => void;
  logout: () => void;
  startLoggingOut: (logoutUri: string) => void;
  userAuthenticatedApiCall: <TResponse>(
    options: StateAuthenticatedApiCallOptions<TResponse>,
  ) => Promise<Result<TResponse, BaseError> | null>;
  loadFeatureFlags: () => void;
  loadUser: (params: {
    observation: ObservationLocationType | null;
    organizationId: string | null;
    onLoaded?: (newObservation: ObservationLocationType | null) => void;
  }) => void;
  callbacks: UserCallbacks;
}

export const UserContext = createContext<UserContext>({
  myUserStatus: null,
  logoutLink: null,
  myUser: null,
  isAuthenticated: false,
  featureFlags: null,
  login: () => {},
  loginWithSSO: () => {},
  logout: () => {},
  startLoggingOut: () => {},
  userAuthenticatedApiCall: async () => null,
  loadUser: () => {},
  loadFeatureFlags: () => {},
  callbacks: new UserCallbacks(),
});

export interface UserRendererProps {
  children: React.ReactNode;
}
